import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { driverApi } from '../../services/driverApi';
import { isIosDevice, resolveEgovLaunchHref } from '../../utils/egovLaunch';
import { DriverCopy, DriverLang } from './driverI18n';

interface DriverLoginPageProps {
  t: DriverCopy;
  lang: DriverLang;
  onLang: (lang: DriverLang) => void;
  onSuccess: () => void;
}

export const DriverLoginPage: React.FC<DriverLoginPageProps> = ({ t, lang, onLang, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [rawLaunchLink, setRawLaunchLink] = useState<string | null>(null);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [demoCode, setDemoCode] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const launchHref = useMemo(
    () => resolveEgovLaunchHref(rawLaunchLink),
    [rawLaunchLink]
  );
  const onIos = isIosDevice();

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const clearAuthUrlParam = useCallback(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('auth')) return;
    url.searchParams.delete('auth');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }, []);

  const setAuthUrlParam = useCallback((id: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('auth', id);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }, []);

  const pollSession = useCallback(
    (id: string) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const session = await driverApi.getEgovAuthSession(id);
          if (session.status === 'COMPLETED' && session.token && session.iin) {
            stopPolling();
            driverApi.setSession(
              { iin: session.iin, fio: session.fio },
              session.token
            );
            const url = new URL(window.location.href);
            url.searchParams.delete('auth');
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);
            onSuccess();
          } else if (session.status === 'EXPIRED' || session.status === 'CANCELED') {
            stopPolling();
            setError(session.status === 'EXPIRED' ? t.loginExpired : t.loginCanceled);
            setSessionId(null);
            setRawLaunchLink(null);
          }
        } catch (err) {
          console.error(err);
        }
      }, 2000);
    },
    [onSuccess, stopPolling, t.loginCanceled, t.loginExpired]
  );

  const startDemo = async () => {
    setError('');
    setLoading(true);
    stopPolling();
    try {
      const result = await driverApi.loginDemo(demoCode.trim());
      driverApi.setSession({ iin: result.iin, fio: result.fio }, result.token);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.loginDemoFailed);
    } finally {
      setLoading(false);
    }
  };

  const startAuth = async () => {
    setError('');
    setLoading(true);
    stopPolling();
    try {
      const result = await driverApi.startEgovAuth();
      setSessionId(result.sessionId);
      setRawLaunchLink(result.eGovMobileLaunchLink);
      setAuthUrlParam(result.sessionId);
      pollSession(result.sessionId);
      const href = resolveEgovLaunchHref(result.eGovMobileLaunchLink);
      if (href) {
        window.location.assign(href);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    driverApi.getDemoLogin().then((demo) => setDemoEnabled(Boolean(demo.enabled))).catch(() => {});
  }, []);

  useEffect(() => {
    const authParam = new URLSearchParams(window.location.search).get('auth');
    if (authParam) {
      setSessionId(authParam);
      pollSession(authParam);
      driverApi.getEgovAuthSession(authParam).then((s) => {
        if (s.eGovMobileLaunchLink) setRawLaunchLink(s.eGovMobileLaunchLink);
      }).catch(() => {});
    }
    return stopPolling;
  }, [pollSession, stopPolling]);

  return (
    <div className="login-container driver-login">
      <div className="driver-login-lang" role="group" aria-label={t.language}>
        <button type="button" className={lang === 'ru' ? 'active' : undefined} onClick={() => onLang('ru')}>
          RU
        </button>
        <button type="button" className={lang === 'kk' ? 'active' : undefined} onClick={() => onLang('kk')}>
          KZ
        </button>
      </div>
      <div className="login-box">
        <div className="login-header">
          <h1>
            <span className="login-title-cashalot">Cashalot</span>
            <span className="login-title-edo">ЭДО</span>
          </h1>
        </div>

        <div className="login-form">
          <p className="driver-login-lead">{t.loginLead}</p>

          {error && <div className="driver-login-error">{error}</div>}

          {!sessionId ? (
            <button type="button" className="btn-login" disabled={loading} onClick={startAuth}>
              {loading ? t.loginPreparing : t.loginBtn}
            </button>
          ) : (
            <div className="driver-login-session">
              <p className="driver-login-lead">{t.loginQrHint}</p>

              {launchHref && (
                <a href={launchHref} className="btn-login driver-login-egov">
                  {t.openEgov}
                </a>
              )}

              {onIos && <p className="driver-login-ios">{t.loginIosHint}</p>}

              <button
                type="button"
                className="btn-reset"
                style={{ width: '100%' }}
                onClick={() => {
                  stopPolling();
                  clearAuthUrlParam();
                  setSessionId(null);
                  setRawLaunchLink(null);
                }}
              >
                {t.loginCancel}
              </button>
            </div>
          )}

          {demoEnabled && !sessionId && (
            <div className="driver-login-demo">
              <p className="driver-login-lead">{t.loginDemoLead}</p>
              <input
                type="password"
                autoComplete="off"
                placeholder={t.loginDemoPlaceholder}
                value={demoCode}
                onChange={(event) => setDemoCode(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') startDemo();
                }}
              />
              <button type="button" className="btn-login" disabled={loading || !demoCode.trim()} onClick={startDemo}>
                {t.loginDemoBtn}
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
